/* eslint-disable no-shadow */
import {
  GridApi,
  IHeaderParams,
  RowNode,
  ValueGetterParams,
} from 'ag-grid-community';
import {
  ColumnFilterModel,
  ColumnFilterParams,
  FilterOptionType,
  InitFilterParams,
  ListColumn,
  Primitive,
  SelectOption,
  XuxGridAction,
  XUX_GRID_ACTIONS,
} from '@geniuspos/ui-shared/modules/xdm-shared-components/definitions';
import { TimeFormats } from '@geniuspos/ui-shared/modules/xdm-shared-components/components/xux-time-picker';

import {
  IFilterQuery,
  CUSTOM_XUX_GRID_ACTIONS,
  IIconOptions,
} from '../../common';
import { CELL_RENDERER } from '../../enums/cell-renderer.enum';
import {
  EntityId,
  IBaseEntityType,
  REFRESH_CELLS_CONDITIONS,
  ToolbarButtonDefinition,
  USER_CONFIG_MODULE_NAME,
  USER_CONFIG_MODULE_TYPE,
} from '@shared/types';
import { XuxEditGridComponent } from '@app/components/xux/xux-edit-grid/xux-edit-grid-widget.component';
import { XUXListContainerComponent } from '@app/containers/base';
import { ColumnState } from 'ag-grid-community/dist/lib/columnController/columnController';
import { IViewSettingModalParams } from '../../entities/stand-worksheet';
import {
  ComponentStaticText,
  I18nStrTypeWithParams,
} from '@app/i18n/i18n.types';
import { TranslateService } from '@ngx-translate/core';
import {
  DashboardModuleKey,
  DndItem,
  IGridItemConfig,
} from '@app/components/xux-dnd/types';
import { Observable } from '@app/shared/rx';

interface IHideColumnConditions {
  [key: string]: boolean;
}

export type ValueFormatterFn = (value: unknown) => string;

export type ValueGetter<T> = ((params: ValueGetterParams) => T) | string;

export interface IXVIColumnFilterParams extends ColumnFilterParams {
  /**
   * Pass `true` in case you need server side filtering.
   * Default value is `false`
   * Notice: not all filters support it for now! Search by the project.
   */
  suppressUIFiltering?: boolean;
  step?: number;
  filterItemsSource?: FilterItemsSourceEnum;
  filterItemSourceQuery?: IFilterQuery;
  valueFormatter?: ValueFormatterFn;
  /**
   * alternative for path prop
   * usable in case path point to array of objects property and we should map it for filtering
   */
  filterValueGetter?: (data: any) => any;

  staticTextContent?: ComponentStaticText;
  filterOptionsMapper?: (
    selectOptions: SelectOption[],
    options: {
      translateService: TranslateService;
    },
  ) => SelectOption[];

  /**
   * use this to control the time format for the filter(working in xux-time-picker now)
   */
  timeFormat?: TimeFormats;
}

export interface FilterStringBuilderParams<T = any> {
  field: string;
  fieldName: string;
  filterModel: ColumnFilterModel;
  filterType: FilterOptionType;
  filterValue: T;
  column: IXVIListColumn;
}

export interface IXVIListColumn
  extends Omit<ListColumn, 'title' | 'cellRenderer' | 'pinnedRowCellRenderer'> {
  /**
   * title
   * Text to be displayed in the column header.
   * When is not defined, the header text will be created from 'path' property.
   */
  title?: string | I18nStrTypeWithParams;
  // if hideConditions are specified the 'hide' property from ColdDef will be ignored
  enableSorting?: boolean;
  canSelected?: boolean;
  selected?: boolean;
  hideConditions?: IHideColumnConditions;
  filterParams?: IXVIColumnFilterParams;
  /**
   * use this to build custom filter string
   */
  filterStringBuilder?: (params: FilterStringBuilderParams) => string;
  pinnedRowCellRenderer?: CELL_RENDERER;
  cellRenderer?: CELL_RENDERER | Function;
  discardParamsToBackend?: boolean;
  sortUseLabelPath?: boolean; // use labelPath to sort column
}

export type DefaultButtonClickHandler = (items: any[]) => Promise<void>;

export enum FilterItemsSourceEnum {
  XPRT_USERS = 'xprt.users',
  XPRT_EVENTS = 'xprt.events',
  XPRT_SITES = 'xprt.site',
}

export enum GridRowPosition {
  TOP = 'top',
}

export enum EDIT_GRID_ITEM_ACTION_TYPE {
  GRID_ITEM_ADDED = 'GRID_ITEM_ADDED',
  GRID_ITEM_EDITED = 'GRID_ITEM_EDITED',
  GRID_ITEMS_REMOVED = 'GRID_ITEMS_REMOVED',
}

export enum GRID_LIST_VIEW_TYPE {
  CATEGORY_VIEW = 'category_view',
  ALPHABETICAL_LIST_VIEW = 'alphabetical_list_view',
}

export enum STAND_WORKSHEET_ENTRY_FORM_VIEW_TYPE {
  SUMMARY_VIEW = 'summary_view',
  DETAILED_VIEW = 'detailed_view',
}

export enum STAND_WORKSHEET_GLOBAL_PRICE_SETTING {
  HIGHEST_PARENT_PRODUCT_PRICE = 'highest_parent_product_price',
  CURRENT_SITE_PRICE = 'current_site_price',
}

export enum DURING_EVENT_TRANSFER_AUTO_COMPLETE_SETTING {
  AUTO_COMPLETE = 'auto_complete',
  NOT_AUTO_COMPLETE = 'do_not_auto_complete',
}

export interface IRestrictionGridItem {
  name: string;
  entity_id: EntityId;
  is_enabled: boolean;
}

export interface IAddedGridItem {
  name: string;
  entity_id: EntityId;
}

export interface IGridRowOrCellClassRule {
  [cssClassName: string]: ((params: any) => boolean) | string;
}

export interface RefreshCellCondition
  extends Partial<Record<REFRESH_CELLS_CONDITIONS, boolean>> {
  [key: string]: boolean;
}

export interface IToolbarButtonDefinition extends ToolbarButtonDefinition {
  disableWithGrid?: boolean;
  isFlattenButtonToActionPanel?: boolean;

  /**
   * is need to omit translation for this button
   */
  omitTranslation?: boolean;

  /**
   * is rendered as an expandable button
   * only working in action panel for now(edit-grid-custom), will be rendered as normal button if used outside of action panel
   */
  expandable?: boolean;

  /**
   * icon config for the expand button when expandable is true,
   * only working in action panel for now(edit-grid-custom), will be ignored if used outside of action panel
   */
  expandIconConfigObs?: Observable<IIconOptions>;
}

export interface IXVIHeaderParams extends IHeaderParams {
  headerIconClick?: () => void;
}

export type EventHandler = (
  action: XuxGridAction,
  grid: XuxEditGridComponent | XUXListContainerComponent,
) => void;

export interface DashboardModules {
  showingColList: IGridItemConfig[];
  hiddenModules: Partial<DndItem>[];
}

export interface DashboardWidgetConfig {
  auto_refresh: boolean;
}

export interface DashboardWidgetModules {
  [DashboardModuleKey.Event]?: DashboardWidgetConfig;
  [DashboardModuleKey.InventoryValue]?: DashboardWidgetConfig;
  [DashboardModuleKey.PurchaseOrders]?: DashboardWidgetConfig;
  [DashboardModuleKey.Transfers]?: DashboardWidgetConfig;
  [DashboardModuleKey.StandWorksheet]?: DashboardWidgetConfig;
  confirmedWidgets: string[];
}

export type ModuleConfigContent = DashboardModules;

export type IUserSetting =
  | ColumnState[]
  | IViewSettingModalParams
  | ModuleConfigContent
  | DashboardWidgetModules;

export interface IUserConfig {
  module_type: USER_CONFIG_MODULE_TYPE;
  module_name: USER_CONFIG_MODULE_NAME;
  module_config: IUserSetting;
  user_id?: string;
  _id?: string;
}

export interface XVIInitFilterParams extends InitFilterParams {
  step?: number;
}

export interface XVIGridAction extends Omit<XuxGridAction, 'type'> {
  type: XUX_GRID_ACTIONS | CUSTOM_XUX_GRID_ACTIONS;
}

export interface IRowParams {
  isDuplicate?: boolean;
}

export enum GridExpandedState {
  Expanded = 'Expanded',
  Collapsed = 'Collapsed',
  Varies = 'Varies',
  None = 'None',
}

export enum GridCellClass {
  HIDDEN_GROUP_ICON = 'hidden-group-icon',
}

export enum GridRowClass {
  INFO_ROW = 'xvi-grid-info-row',
  WARNING_ROW = 'xvi-grid-warn-row',
  ERROR_ROW = 'xvi-grid-error-row',
}

export interface IXuxListGridOptions {
  hiddenGridHeaderCheckbox?: boolean;
  expandedMaxLevel?: number;
  enableDynamicExpandBtn?: boolean;
  /**
   * field to sync after filter applied(for tree edit mode now)
   */
  syncTreeFieldAfterFilter?: string;

  /**
   * whether keep the tree structure when save, only for tree edit mode,
   * default is false, which means will save the data as flat children list,
   * if set to true, will save the data with tree structure,
   * which means the children will be saved in a field of parent node, and the field name is defined by treeChildrenField in column definition
   */
  keepTreeStructureWhenSave?: boolean;

  /**
   * treeTotalLevel - number of levels in tree, if treeViewMode is true. If not set, tree will be builded with all levels from data to calc totals
   */
  treeTotalLevel?: number;

  /**
   * is keep the node expanded when the node data is updated, only for tree edit mode,
   * default is false, which means the node will be expanded when not all nodes collapsed
   */
  keepNodeOwnExpanded?: boolean;

  /**
   * customizeExpandFn - function for customize expanded state for tree data, will be called on grid ready and after expand/collapse group with params
   *
   * will return true if already handled expanded.
   */
  customizeExpandFn?: (params: {
    gridApi: GridApi;
    isInitExpand?: boolean;
    expanded?: boolean;
    maxLevel?: number;
  }) => boolean;
  /**
   * Whether to enable the default expand function, which expands the tree when the grid is initialized.
   * Default is false.
   */
  enableDefaultExpandFn?: boolean;
}

export type FormListData =
  | Record<EntityId, IBaseEntityType>
  | IBaseEntityType[];

export interface IShouldUpdateParams {
  node: RowNode;
  rowField: string;
  rowValue: Primitive;
  gridApi: GridApi;
  direction: 'parent' | 'child';
}
export interface IUpdateRowNodeParams {
  gridApi: GridApi;
  rowValue: Primitive;
  rowField: string;
  recursively?: boolean;
  refreshGrid?: boolean;
  /**
   * function to decide whether should update the node or not
   */
  shouldUpdate?: (shouldUpdateParams: IShouldUpdateParams) => boolean;
}

export interface GetAllRowsOptions<T = any> {
  withFilterAndSort?: boolean;
  selector?: (node: RowNode) => boolean;
  valueSelector?: (node: RowNode) => T;
}
